import { FetchError } from '@lib/types/api/FetchError';
import { isTest } from '../env';

const defaultHeaders = {
  'Content-Type': 'application/json',
  'x-client-id': 'compass-ui',
};

const buildFetchUrl = (url: string) => {
  // append base url when running in test since Mock Service Worker doesn't resolve relative urls in Node environments
  return isTest() && url.startsWith('/') ? `http://localhost:3000${url}` : url;
};

/**
 * @throws {FetchError}
 */
export const failingFetcher = async (
  url,
  options?: RequestInit,
  rawResponse = false
) => {
  const fetchUrl = buildFetchUrl(url);
  const { body, headers, method, ...restOptions } = options || {};

  const response = await fetch(fetchUrl, {
    method,
    headers: new Headers({
      ...defaultHeaders,
      ...headers,
    }),
    body,
    ...restOptions,
  });

  if (!response.ok) {
    let code: string | undefined;
    try {
      const body = await response.json();
      console.error(
        `${fetchUrl} ${response.status} ${response.statusText}`,
        body
      );
      code = typeof body?.code === 'string' ? body.code : undefined;
    } catch (e) {}

    throw new FetchError(
      `${url}: ${response.status} ${response.statusText}`,
      response.status,
      code
    );
  }

  try {
    if (rawResponse) {
      return response;
    }
    const body = await response.json();
    if (body) {
      return body;
    }
  } catch (error) {
    // if there is no body to parse from backend .json() can throw JSON error
    // response still succeeded so simply return empty body
    return {};
  }

  return {};
};

export const swrFetcher = (endpoint: string, init?: RequestInit) => {
  // SWR hands this fetcher any key that has no fetcher of its own, arrays
  // included. `fetch` would stringify `['a', 'b']` into the relative URL `a,b`
  // and request it from the current page, so fail loudly instead.
  if (typeof endpoint !== 'string') {
    return Promise.reject(
      new TypeError(
        `swrFetcher expects a URL string, got ${JSON.stringify(endpoint)}`
      )
    );
  }
  const fetchUrl = buildFetchUrl(endpoint);

  return fetch(fetchUrl, init).then(async (res) => {
    if (!res.ok) {
      throw await res.json();
    }
    return res.json();
  });
};

export const swrMutationFetcher = (endpoint: string, init?: RequestInit) => {
  const fetchUrl = buildFetchUrl(endpoint);
  const { method = 'POST', headers, ...rest } = init ?? {};

  return fetch(fetchUrl, {
    method,
    headers: headers ?? {
      'Content-Type': 'application/json',
    },
    ...rest,
  }).then(async (res) => {
    if (!res.ok) {
      throw await res.json();
    }
    return res.json();
  });
};
